import React from 'react'
import { GetServerSideProps } from 'next'
import { Product } from '@mc/api'
import { BogoBanner } from '@mc/bogo'
import { Separator } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { getBucket } from '@mc/next-growthbook'
import { PAGE_SOURCES, PromoExplainerModal } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { CategoryNavCarousel } from 'components/CategoryNavCarousel'
import { QuizEntryModalLoader } from 'experiments/pen-5646_v1_quiz_entry_sticky_footer/client'
import {
  getModalFeaturedClasses,
  QUIZ_ENTRY_IN_STICKY_FOOTER_EXP_FLAG,
} from 'experiments/pen-5646_v1_quiz_entry_sticky_footer/server'
import { articleCategoryPath } from 'libs/paths'
import {
  getArticlesHomepageCategories,
  getArticles,
  getFeaturedArticle,
  getLatestArticles,
  getArticlesCount,
  getDefaultProduct,
  getV2Categories,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import type {
  ArticleItem,
  ArticlesCount,
  Category,
  FeaturedArticleItem,
} from 'types/data'
import { CategoryList } from './components/CategoryList'
import Details from './components/Details'
import { FeaturedArticle } from './components/FeaturedArticle'
import { InstructorTilesHero } from './components/InstructorTilesHero'
import Meta from './components/Meta'
import StickyFooter from './components/StickyFooter'
import { trackCategoryCarouselClick } from './components/events'

interface ArticlesProps {
  categories: Category[]
  annualPass: Product
  topArticles: Array<ArticleItem>
  totalArticles: ArticlesCount
  latestArticles: Array<ArticleItem>
  featuredArticle: FeaturedArticleItem
  articleCategories: Awaited<ReturnType<typeof getArticlesHomepageCategories>>
  quizEntryModalFeaturedClasses: Awaited<
    ReturnType<typeof getModalFeaturedClasses>
  >
}

const Articles = ({
  annualPass,
  topArticles,
  latestArticles,
  totalArticles: { count: totalLatestArticles },
  featuredArticle: { featuredArticle },
  articleCategories,
  quizEntryModalFeaturedClasses,
}: ArticlesProps) => {
  const { t } = useTranslation('@mc/next')

  return (
    <StaticDataProvider
      value={{
        annualPass,
        featuredArticle,
        latestArticles,
        topArticles,
        totalLatestArticles,
        quizEntryModalFeaturedClasses,
      }}
    >
      <TrackingParamsProvider
        value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-articles' }}
      >
        <Meta />
        <Separator className='mc-opacity--muted mc-m-0' />
        <BogoBanner />
        <PromoExplainerModal pageSource={PAGE_SOURCES.ARTICLES_PAGE} />
        <QuizEntryModalLoader />

        <div className='container'>
          <div className='mc-my-2'>
            <CategoryNavCarousel
              categories={articleCategories}
              pathCategory={articleCategoryPath}
              onNavClick={trackCategoryCarouselClick}
            />
          </div>
        </div>
        <InstructorTilesHero />
        <div className='mc-theme-light mc-bg-light'>
          <FeaturedArticle
            featuredArticle={featuredArticle}
            heading={t('articles.featuredArticles.sectionTitle')}
          />
          <CategoryList categories={articleCategories} />
          <Details />
        </div>
        <StickyFooter />
      </TrackingParamsProvider>
    </StaticDataProvider>
  )
}

Articles.pageSettings = {
  analyticsLabel: 'Articles Landing',
  showPromo: true,
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const quizEntryExperimentBucket = await getBucket(
    ctx,
    QUIZ_ENTRY_IN_STICKY_FOOTER_EXP_FLAG,
  )

  const shouldFetchQuizEntryClasses =
    quizEntryExperimentBucket?.startsWith('variant')

  return getPageData({
    nonCriticalPromises: {
      categories: getV2Categories(ctx),
      annualPass: getDefaultProduct(ctx),
      topArticles: getArticles(ctx),
      latestArticles: getLatestArticles(ctx),
      featuredArticle: getFeaturedArticle(ctx),
      totalArticles: getArticlesCount(ctx),
      articleCategories: getArticlesHomepageCategories(ctx),
      quizEntryModalFeaturedClasses: shouldFetchQuizEntryClasses
        ? getModalFeaturedClasses(ctx)
        : Promise.resolve([]),
    },
    propsMapper: ({ ...restProps }) => ({
      ...restProps,
    }),
  })
}

export default Articles
